import type {Page} from '@PosterWhiteboard/page/page.class';
import type {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import type {GA4EventSources} from '@Libraries/ga-events';

export interface TextOnPathDrawRequest {
  /** The page the path is drawn on. */
  page: Page;
  /** An existing text item whose path each stroke redraws (its text kept); every stroke makes a new item with placeholder text when omitted. */
  item?: TextItem;
  /** The editor surface the user opened the mode from, for analytics. */
  source: GA4EventSources;
}

/** What the Text on path panel and the undo/redo buttons mirror while paths are being drawn. */
export interface TextOnPathModeEntry {
  pageHashedId: string;
  /** Opened from a selected text's Shapes block to redraw its path, rather than to draw new text. */
  isRedrawingText: boolean;
  /** A stroke of this session can be taken back (which also means one has been drawn). */
  canUndo: boolean;
  /** A stroke this session took back can be put back. */
  canRedo: boolean;
}

export type TextOnPathModeHistory = Omit<TextOnPathModeEntry, 'pageHashedId' | 'isRedrawingText'>;

/**
 * Redrawing an existing item's path sizes its text to run the stroke end to end, but never larger
 * than this many times the size it had when drawing opened: a short word on a long stroke would
 * otherwise get letters taller than the stroke's bends, which distorts them.
 */
export const TEXT_ON_PATH_REDRAW_MAX_FONT_GROWTH = 3;

/**
 * The stroke the user draws: a thin dark line on the upper canvas, as wide on screen whatever the
 * zoom, in a light halo so it reads on a dark page too (on a navy page the stroke was invisible
 * until it became a text item, 2026-10-29).
 */
export const TEXT_ON_PATH_BRUSH_COLOR = '#2c2b44';
export const TEXT_ON_PATH_BRUSH_SCREEN_WIDTH = 2;
export const TEXT_ON_PATH_BRUSH_HALO_COLOR = '#ffffff';
export const TEXT_ON_PATH_BRUSH_HALO_SCREEN_BLUR = 4;
